import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap, Check, Loader2 } from "lucide-react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

export default function BuyCredits() {
  const [packs, setPacks] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);
  const { user } = useAuth();
  const activeWs = user?.active_workspace;
  const [target, setTarget] = useState(activeWs ? "workspace" : "personal");
  const CUSTOM_MIN = 5, CUSTOM_MAX = 1000;
  const CUSTOM_TIERS = [[5, 49, 0.45], [50, 149, 0.40], [150, 499, 0.35], [500, 1000, 0.30]];
  const customRate = (c) => (CUSTOM_TIERS.find(([lo, hi]) => c >= lo && c <= hi) || CUSTOM_TIERS[0])[2];
  const [custom, setCustom] = useState(75);
  const rate = customRate(custom);
  const customPrice = (custom * rate).toFixed(2);

  useEffect(() => {
    api.get("/credit-packs").then((r) => setPacks(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setTarget(activeWs ? "workspace" : "personal");
  }, [activeWs?.id]);

  const buy = async (pack) => {
    setLoadingKey(pack.lookup_key);
    try {
      const body = { lookup_key: pack.lookup_key, origin_url: window.location.origin };
      if (target === "workspace" && activeWs) body.workspace_id = activeWs.id;
      const res = await api.post("/payments/checkout", body);
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout", { description: e?.response?.data?.detail || "Try again." });
      setLoadingKey(null);
    }
  };

  const buyCustom = async () => {
    const credits = Math.max(CUSTOM_MIN, Math.min(CUSTOM_MAX, parseInt(custom) || 0));
    setLoadingKey("custom");
    try {
      const body = { credits, origin_url: window.location.origin };
      if (target === "workspace" && activeWs) body.workspace_id = activeWs.id;
      const res = await api.post("/payments/checkout", body);
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout", { description: e?.response?.data?.detail || "Try again." });
      setLoadingKey(null);
    }
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
            <Zap className="h-3.5 w-3.5 text-amber-400" /> Buy Credits
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Top up your account</h1>
          {user && (
            <p className="text-slate-600 mt-3">
              {target === "workspace" && activeWs
                ? <>Adding to <span className="font-semibold">{activeWs.name}</span>'s shared pool (currently <span className="font-mono font-semibold text-slate-900">{activeWs.credits} credits</span>). Team exports add a +1 credit premium on top of each tool's cost.</>
                : <>You currently have <span className="font-mono font-semibold text-slate-900">{user.credits} credits</span>. Each personal PDF costs 1 credit.</>}
            </p>
          )}
          {activeWs && (
            <div className="inline-flex bg-slate-100 rounded-full p-1 mt-5" data-testid="topup-target-toggle">
              <button data-testid="topup-personal" onClick={() => setTarget("personal")}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${target === "personal" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-600"}`}>
                My credits
              </button>
              <button data-testid="topup-workspace" onClick={() => setTarget("workspace")}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${target === "workspace" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-600"}`}>
                {activeWs.name} pool
              </button>
            </div>
          )}
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {packs.map((p) => (
            <div
              key={p.lookup_key}
              className={`relative bg-white rounded-2xl border p-8 flex flex-col ${
                p.popular ? "border-indigo-600 ring-2 ring-indigo-600 shadow-xl md:-translate-y-2" : "border-slate-200"
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Most Popular
                </span>
              )}
              <h3 className="font-display text-lg font-semibold">{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-4xl font-bold">${p.price}</span>
              </div>
              <p className="text-slate-500 mt-1 font-mono text-sm">{p.credits} credits</p>
              <p className="text-slate-400 text-xs mt-1">${(p.price / p.credits).toFixed(2)} per PDF</p>
              <div className="mt-6 flex-1 space-y-2 text-sm text-slate-600">
                <p className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> {p.credits} PDF exports</p>
                <p className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Credits never expire</p>
              </div>
              <Button
                data-testid={`stripe-checkout-${p.lookup_key}`}
                onClick={() => buy(p)}
                disabled={loadingKey === p.lookup_key}
                className={`mt-6 rounded-full ${p.popular ? "bg-indigo-600 hover:bg-indigo-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
              >
                {loadingKey === p.lookup_key ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Buy {p.credits} credits
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-8" data-testid="custom-credits-card">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h3 className="font-display text-lg font-semibold">Custom amount</h3>
              <p className="text-slate-500 text-sm mt-1">Need a specific amount? Slide to choose — the more you buy, the less per credit.</p>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-bold" data-testid="custom-price">${customPrice}</div>
              <div className="text-slate-500 font-mono text-sm"><span data-testid="custom-credits-value">{custom}</span> credits · <span data-testid="custom-rate">${rate.toFixed(2)}</span>/ea</div>
            </div>
          </div>
          <input
            type="range" min={CUSTOM_MIN} max={CUSTOM_MAX} step={5} value={custom}
            onChange={(e) => setCustom(parseInt(e.target.value))}
            data-testid="custom-credits-slider"
            className="w-full mt-6 accent-indigo-600 cursor-pointer"
          />
          <div className="flex items-center justify-between text-xs text-slate-400 mt-1 font-mono">
            <span>{CUSTOM_MIN}</span><span>{CUSTOM_MAX}</span>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-xs text-slate-500">
            {CUSTOM_TIERS.map(([lo, hi, r]) => (
              <span key={lo} className={custom >= lo && custom <= hi ? "text-indigo-600 font-semibold" : ""}>
                {lo}–{hi}: ${r.toFixed(2)}/ea
              </span>
            ))}
          </div>
          <Button
            data-testid="stripe-checkout-custom"
            onClick={buyCustom}
            disabled={loadingKey === "custom"}
            className="mt-6 w-full sm:w-auto rounded-full bg-slate-900 hover:bg-slate-800 text-white px-8"
          >
            {loadingKey === "custom" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Buy {custom} credits · ${customPrice}
          </Button>
        </div>

        <p className="text-center text-slate-400 text-xs mt-8 font-mono">
          Test card: 4242 4242 4242 4242 · any future expiry · any CVC
        </p>
      </div>
    </div>
  );
}
